import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, UtensilsCrossed, ChevronDown } from "lucide-react";
import { IMAGES, RESTAURANT } from "../data/site";
import { useContent } from "@/context/ContentContext";
import { resolveImg } from "@/lib/api";
import { scrollToId } from "../lib/scroll";

const headline = ["Rainey's", "Soul Food LLC"];

const go = (id) => scrollToId(id);

export const Hero = () => {
  const { content } = useContent();
  const heroImg = content?.images?.hero || IMAGES.hero;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.7, 0.9]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-center justify-center overflow-hidden"
    >
      <motion.img
        src={resolveImg(heroImg)}
        alt="Rainey's signature slow-braised oxtails with homemade sides"
        style={{ y, scale: 1.15 }}
        className="absolute inset-0 w-full h-full object-cover"
      />
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-charcoal" />
      <div className="absolute inset-0 bg-gradient-to-t from-burgundy-dark/85 via-charcoal/55 to-charcoal/70" />

      <div className="relative z-10 text-center px-6 max-w-5xl">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="font-body text-cream/80 uppercase tracking-[0.35em] text-xs md:text-sm mb-6"
        >
          Family-Owned · Hampton, Virginia
        </motion.p>

        <h1 data-testid="hero-heading" className="font-script text-gold-light">
          {headline.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                className="block text-5xl sm:text-7xl md:text-8xl lg:text-9xl leading-[1.12] pt-[0.24em] pb-[0.14em] px-[0.08em]"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, delay: 0.35 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-4 font-script text-gold-light text-2xl sm:text-3xl md:text-4xl max-w-3xl mx-auto leading-tight"
        >
          Bringing Soulful Flavors Wherever You Celebrate
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          className="mt-11 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <button
            data-testid="hero-view-menu-btn"
            onClick={() => go("menu")}
            className="group inline-flex items-center gap-2.5 bg-burgundy text-cream px-9 py-4 rounded-full font-body font-semibold tracking-wide hover:bg-burgundy-light transition-all duration-300 hover:scale-[1.04] shadow-[0_12px_30px_rgba(122,28,34,0.35)]"
          >
            <UtensilsCrossed size={18} /> View Menu
          </button>
          <a
            data-testid="hero-directions-btn"
            href={RESTAURANT.directionsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2.5 bg-cream/10 border border-cream/60 text-cream backdrop-blur-sm px-9 py-4 rounded-full font-body font-semibold tracking-wide hover:bg-cream hover:text-burgundy transition-all duration-300"
          >
            <MapPin size={18} /> Get Directions
          </a>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-cream/70"
      >
        <ChevronDown className="animate-bounce" size={26} />
      </motion.div>
    </section>
  );
};
